import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isHQ, ownBranches, canManagePackage } from "@/lib/branchScope";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, PackageIcon } from "lucide-react";

// Gross = what the patient is quoted at the counter. The catalogue stores the
// ex-tax price (matching InvoiceItem.rate), but every conversation about a
// package happens in gross, so both are shown everywhere.
const grossOf = (price, taxPercent) =>
  Math.round((Number(price || 0) * (1 + Number(taxPercent || 0) / 100)) * 100) / 100;

export default function Packages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    // active_only=false: admins manage deactivated packages here too.
    api.get("/packages", { params: { active_only: false } })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [load]);

  const toggleActive = async (pkg, active) => {
    try {
      await api.put(`/packages/${pkg.id}`, { active });
      toast.success(active ? "Package activated" : "Package deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete package "${pkg.name}"?\n\nIf patients already hold it, it will be deactivated instead so their records keep working.`)) return;
    try {
      const { data } = await api.delete(`/packages/${pkg.id}`);
      toast.success(data.message || "Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || id;
  // Branch managers / reception manage only packages sold exclusively at their
  // own branches (backend: _check_branch_scope); the rest are read-only here.
  const hq = isHQ(user);
  const myBranches = ownBranches(user, branches);
  const canCreate = hq || (user?.branch_ids || []).length > 0;
  const term = search.trim().toLowerCase();
  const filtered = term
    ? items.filter((p) => `${p.name} ${p.code} ${p.category || ""}`.toLowerCase().includes(term))
    : items;

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-cream/50 px-4 py-3">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search code, name or category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 bg-white pl-9"
              data-testid="packages-search"
            />
          </div>
          <Button onClick={() => setCreating(true)} disabled={!canCreate} title={canCreate ? undefined : "No branch is assigned to your account"}
            className="bg-navy text-white hover:bg-navy-hover" data-testid="new-package-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Add Package
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2 text-right">Sessions</th>
                <th className="px-4 py-2 text-right">Price (ex-GST)</th>
                <th className="px-4 py-2 text-right">GST</th>
                <th className="px-4 py-2 text-right">Gross</th>
                <th className="px-4 py-2">Validity</th>
                <th className="px-4 py-2">Branches</th>
                <th className="px-4 py-2 text-center">Active</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-muted-foreground">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-muted-foreground">
                  {term ? "No packages match that search" : "No packages yet — add the ones you sell"}
                </td></tr>
              ) : filtered.map((p) => {
                const manage = canManagePackage(user, p);
                return (
                <tr key={p.id} className={`border-b last:border-0 hover:bg-nude/30 ${p.active ? "" : "opacity-55"}`} data-testid={`package-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{p.code}</td>
                  <td className="px-4 py-3 font-medium text-ink">{p.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{p.sessions}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{formatCurrency(p.price)}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{p.tax_percent}%</td>
                  <td className="px-4 py-3 text-right font-semibold text-navy">{formatCurrency(p.gross_price)}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {p.validity_days ? `${p.validity_days} days` : "No expiry"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {!p.branch_ids?.length
                      ? <span className="text-xs">All branches</span>
                      : <span className="text-xs">{p.branch_ids.map(branchName).join(", ")}</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch checked={!!p.active} onCheckedChange={(v) => toggleActive(p, v)} disabled={!manage} data-testid={`package-active-${p.id}`} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {manage ? (
                      <>
                        <button onClick={() => setEditing(p)} className="rounded p-1.5 text-navy/60 hover:bg-navy/10" data-testid={`edit-package-${p.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => remove(p)} className="rounded p-1.5 text-red-500 hover:bg-red-50" data-testid={`delete-package-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground" title="Sold at all branches or at a branch you don't manage"
                        data-testid={`package-hq-only-${p.id}`}>Managed by HQ</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <PackageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Selling a package copies its price and session count onto the patient's record, so changing a
        price here never alters what an existing patient agreed to pay.
      </p>

      <PackageDialog open={creating} pkg={null} branches={myBranches} restricted={!hq} onClose={() => setCreating(false)} onDone={load} />
      <PackageDialog open={!!editing} pkg={editing} branches={myBranches} restricted={!hq} onClose={() => setEditing(null)} onDone={load} />
    </div>
  );
}

// `restricted` (branch manager / reception): `branches` is already just theirs,
// at least one is required, and there is no "All branches" (that stays HQ's).
function PackageDialog({ open, pkg, branches, restricted, onClose, onDone }) {
  const empty = {
    code: "", name: "", category: "", service: "", sessions: 1,
    price: 0, tax_percent: 18, validity_days: 0, branch_ids: [], description: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!pkg;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(pkg ? {
      code: pkg.code || "", name: pkg.name || "", category: pkg.category || "",
      service: pkg.service || "", sessions: pkg.sessions ?? 1, price: pkg.price ?? 0,
      tax_percent: pkg.tax_percent ?? 18, validity_days: pkg.validity_days ?? 0,
      branch_ids: pkg.branch_ids || [], description: pkg.description || "",
    } : { ...empty, branch_ids: restricted ? branches.map((b) => b.id) : [] });
  }, [open, pkg]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Package name is required"); return; }
    if (!isEdit && !form.code.trim()) { toast.error("Package code is required"); return; }
    if (restricted && !form.branch_ids.length) { toast.error("Pick at least one of your branches"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        sessions: parseInt(form.sessions, 10) || 1,
        price: parseFloat(form.price) || 0,
        tax_percent: parseFloat(form.tax_percent) || 0,
        validity_days: parseInt(form.validity_days, 10) || 0,
      };
      if (isEdit) {
        delete body.code;   // snapshotted onto sold treatments; never editable
        await api.put(`/packages/${pkg.id}`, body);
        toast.success("Package updated");
      } else {
        await api.post("/packages", body);
        toast.success("Package created");
      }
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const gross = grossOf(form.price, form.tax_percent);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">{isEdit ? "Edit Package" : "Add Package"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code *">
              <Input
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                placeholder="LHR-FB-06"
                disabled={isEdit}
                data-testid="package-code"
              />
              {isEdit && <p className="mt-1 text-xs text-muted-foreground">Locked — sold packages reference it.</p>}
            </Field>
            <Field label="Category">
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Laser" />
            </Field>
          </div>
          <Field label="Name *">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Laser Hair Reduction — Full Body" data-testid="package-name" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Sessions *">
              <Input type="number" min={1} value={form.sessions} onChange={(e) => set("sessions", e.target.value)} data-testid="package-sessions" />
            </Field>
            <Field label="Price (ex-GST)">
              <Input type="number" inputMode="decimal" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="package-price" />
            </Field>
            <Field label="GST %">
              <Input type="number" inputMode="decimal" value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} />
            </Field>
          </div>
          <div className="rounded-lg bg-cream/60 px-3 py-2 text-center">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Patient pays </span>
            <span className="font-display text-lg font-semibold text-navy" data-testid="package-gross">{formatCurrency(gross)}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Validity (days)">
              <Input type="number" min={0} value={form.validity_days} onChange={(e) => set("validity_days", e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">0 = never expires</p>
            </Field>
            <Field label="Branches">
              <MultiSelect
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
                selected={form.branch_ids}
                onChange={(v) => set("branch_ids", v)}
                placeholder={restricted ? "Select your branch" : "All branches"}
                className="w-full"
              />
              {restricted && <p className="mt-1 text-xs text-muted-foreground">Your branches only. Packages for all branches are managed by HQ.</p>}
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-package-btn">
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
